import { useEffect, useCallback, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Plus, Eye } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { api } from '../services/api'
import Button from './Button'
import SortableBlock from './SortableBlock'
import TranslationBanner from './TranslationBanner'
import PostTranslationBanner from './PostTranslationBanner'
import ContentMetaSidebar from './ContentMetaSidebar'
import ProductMetaTabs from './ProductMetaTabs'
import { useBlockBuilder } from '../hooks/useBlockBuilder'
import usePageBuilderStore from '../stores/pageBuilderStore'
import { useLanguageStore } from '../stores/languageStore'
import { useContentMetaStore } from '../stores/contentMetaStore'
import { getEntityConfig } from '../config/entityConfigs'

const centsToDisplay = (cents) => {
  if (cents === '' || cents == null) return ''
  return (Number(cents) / 100).toFixed(2)
}

const normalizeCategoryIds = (categories) => {
  if (!Array.isArray(categories)) return []
  return [
    ...new Set(
      categories
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id) && id > 0),
    ),
  ].sort((a, b) => a - b)
}

const normalizeRelationIds = (values) => {
  if (!Array.isArray(values)) return []
  return [
    ...new Set(
      values
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id) && id > 0),
    ),
  ].sort((a, b) => a - b)
}

const getProductMetaFromEntity = (entity) => ({
  sku: entity?.sku || '',
  type: entity?.type || 'simple',
  status: entity?.status || 'draft',
  is_featured: !!entity?.is_featured,
  name: entity?.name || '',
  slug: entity?.slug || '',
  short_description: entity?.short_description || '',
  description: entity?.description || '',
  price: centsToDisplay(entity?.price),
  compare_at_price: centsToDisplay(entity?.compare_at_price),
  track_inventory: entity?.track_inventory ?? true,
  stock_quantity: entity?.stock_quantity ?? 0,
  low_stock_threshold: entity?.low_stock_threshold ?? 5,
  allow_backorders: !!entity?.allow_backorders,
  featured_image: entity?.featured_image?.url || entity?.featured_image || '',
  meta_title: entity?.meta_title || '',
  meta_description: entity?.meta_description || '',
  tax_class: entity?.tax_class || 'standard',
  weight: entity?.weight ?? '',
  categories: (entity?.categories || []).map((c) => c.id),
  cross_sells: (entity?.cross_sells || []).map((p) => p.id),
  template_id: null,
})

const getTemplateMetaFromEntity = (entity) => ({
  name: entity?.name || '',
  description: entity?.description || '',
  thumbnail: entity?.thumbnail || '',
  target_entity_type: entity?.target_entity_type || 'product',
  is_active: entity?.is_active ?? true,
  sync_blocks: entity?.sync_blocks ?? true,
  sort_order: entity?.sort_order ?? 0,
})

const normalizeProductMeta = (meta) => ({
  sku: meta?.sku || '',
  type: meta?.type || 'simple',
  status: meta?.status || 'draft',
  is_featured: !!meta?.is_featured,
  name: meta?.name || '',
  slug: meta?.slug || '',
  short_description: meta?.short_description || '',
  description: meta?.description || '',
  price: meta?.price === '' || meta?.price == null ? '' : String(meta.price),
  compare_at_price:
    meta?.compare_at_price === '' || meta?.compare_at_price == null
      ? ''
      : String(meta.compare_at_price),
  track_inventory: meta?.track_inventory ?? true,
  stock_quantity: Number(meta?.stock_quantity ?? 0),
  low_stock_threshold: Number(meta?.low_stock_threshold ?? 5),
  allow_backorders: !!meta?.allow_backorders,
  featured_image:
    typeof meta?.featured_image === 'string'
      ? meta.featured_image
      : meta?.featured_image?.url || '',
  meta_title: meta?.meta_title || '',
  meta_description: meta?.meta_description || '',
  tax_class: meta?.tax_class || 'standard',
  weight:
    meta?.weight === '' || meta?.weight == null ? '' : String(meta.weight),
  categories: normalizeCategoryIds(meta?.categories),
  cross_sells: normalizeRelationIds(meta?.cross_sells),
  template_id: meta?.template_id ? Number(meta.template_id) : null,
})

const normalizeTemplateMeta = (meta) => ({
  name: meta?.name || '',
  description: meta?.description || '',
  thumbnail: meta?.thumbnail || '',
  target_entity_type: meta?.target_entity_type || 'product',
  is_active: meta?.is_active ?? true,
  sync_blocks: meta?.sync_blocks ?? true,
  sort_order: Number(meta?.sort_order ?? 0),
})

const normalizeMetaForEntity = (entityType, meta) => {
  if (entityType === 'product') return normalizeProductMeta(meta)
  if (entityType === 'template') return normalizeTemplateMeta(meta)
  return {}
}

const getMetaFromEntity = (entityType, entity) => {
  if (entityType === 'product') return getProductMetaFromEntity(entity)
  if (entityType === 'template') return getTemplateMetaFromEntity(entity)
  return {}
}

export default function ContentBuilder({ entityType }) {
  const { id } = useParams()
  const location = useLocation()
  const routeId = id ?? (location.pathname.endsWith('/new') ? 'new' : undefined)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isNew = routeId === 'new'

  const config = getEntityConfig(entityType)
  const useStore = useContentMetaStore(entityType)

  const meta = useStore((state) => state.meta)
  const setMeta = useStore((state) => state.setMeta)
  const resetMeta = useStore((state) => state.resetMeta)
  const setSaveHandler = useStore((state) => state.setSaveHandler)
  const setSaveDraftHandler = useStore((state) => state.setSaveDraftHandler)
  const setPublishHandler = useStore((state) => state.setPublishHandler)
  const setDiscardDraftHandler = useStore(
    (state) => state.setDiscardDraftHandler,
  )
  const setIsSaving = useStore((state) => state.setIsSaving)
  const setIsPublishing = useStore((state) => state.setIsPublishing)
  const setIsDiscarding = useStore((state) => state.setIsDiscarding)

  const [isDiscardingState, setIsDiscardingState] = useState(false)
  const [metaBaseline, setMetaBaseline] = useState(null)

  const {
    currentLanguageCode: locale,
    _hasHydrated,
    initializeLanguage,
  } = useLanguageStore()

  const setViewMode = usePageBuilderStore((state) => state.setViewMode)
  const setDraftStates = usePageBuilderStore((state) => state.setDraftStates)
  const clearLocalBlocks = usePageBuilderStore(
    (state) => state.clearLocalBlocks,
  )
  const setIsViewModeChanging = usePageBuilderStore(
    (state) => state.setIsViewModeChanging,
  )
  const setIsLocaleChanging = usePageBuilderStore(
    (state) => state.setIsLocaleChanging,
  )
  const setHasUnsavedChanges = usePageBuilderStore(
    (state) => state.setHasUnsavedChanges,
  )

  const {
    blocks,
    blocksLoading,
    selectedBlockId,
    hasDraftStates,
    viewMode,
    draftStates,
    localBlocks,
    setSelectedBlockId,
    createBlock,
    updateBlock,
    deleteBlock,
    handleFieldChange,
    handleSaveDrafts,
    handlePublish,
    handleDiscard,
    handleDetachPreset,
    isSaving,
    isPublishing,
  } = useBlockBuilder({
    entityType: config.singular,
    entityId: routeId,
    isNew,
    blockField: config.blockField,
    selectedTemplateId: entityType === 'product' ? meta.template_id : null,
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const buildPayload = useCallback(() => {
    if (entityType === 'product') {
      const toCents = (value, fallback = null) => {
        if (value === '' || value == null) return fallback
        const num = parseFloat(value)
        if (Number.isNaN(num)) return fallback
        return Math.round(num * 100)
      }

      return {
        sku: meta.sku,
        type: meta.type,
        status: meta.status,
        is_featured: !!meta.is_featured,
        locale,
        name: meta.name,
        slug: meta.slug,
        short_description: meta.short_description || null,
        description: meta.description || null,
        price: toCents(meta.price, 0) ?? 0,
        compare_at_price: toCents(meta.compare_at_price, null),
        track_inventory: meta.track_inventory ?? true,
        stock_quantity: Number(meta.stock_quantity) || 0,
        low_stock_threshold: Number(meta.low_stock_threshold) || 5,
        allow_backorders: !!meta.allow_backorders,
        featured_image: meta.featured_image || null,
        meta_title: meta.meta_title || null,
        meta_description: meta.meta_description || null,
        tax_class: meta.tax_class || 'standard',
        weight: meta.weight ? Number(meta.weight) : null,
        categories:
          Array.isArray(meta.categories) && meta.categories.length
            ? meta.categories
            : null,
        cross_sells: Array.isArray(meta.cross_sells) ? meta.cross_sells : [],
        template_id: isNew ? meta.template_id || null : null,
      }
    }

    if (entityType === 'template') {
      return {
        name: meta.name,
        description: meta.description || null,
        thumbnail: meta.thumbnail || null,
        target_entity_type: meta.target_entity_type || 'product',
        is_active: meta.is_active ?? true,
        sync_blocks: meta.sync_blocks ?? true,
        sort_order: Number(meta.sort_order) || 0,
      }
    }

    return {
      ...meta,
      locale: locale,
      translations: [
        {
          locale: locale,
          title: meta.title,
          slug: meta.slug,
          excerpt: meta.excerpt,
          content: '',
          meta_description: meta.meta_description,
        },
      ],
      categories: meta.categories.map((cat) => cat.id),
    }
  }, [entityType, isNew, locale, meta])

  const { data: entityRes } = useQuery({
    queryKey: [config.api.queryKeys.single, routeId, locale],
    queryFn: () =>
      api
        .get(config.api.endpoints.single.replace(':id', routeId), {
          params: { locale },
        })
        .then((r) => r.data),
    enabled: !!routeId && !isNew,
  })

  const { data: languagesRes } = useQuery({
    queryKey: ['languages'],
    queryFn: () => api.get('/languages').then((r) => r.data),
  })

  const entity = entityRes?.data || null
  const languages = Array.isArray(languagesRes?.data)
    ? languagesRes.data
    : Array.isArray(languagesRes)
      ? languagesRes
      : []

  const hasTranslation = entity?.has_translation_for_locale ?? true
  const currentLanguage = languages.find((lang) => lang.code === locale)
  const languageName =
    currentLanguage?.name || locale?.toUpperCase() || 'Unknown'
  const shouldTrackMetaDirty =
    entityType === 'product' || entityType === 'template'

  useEffect(() => {
    if (isNew || !entity || !shouldTrackMetaDirty) {
      setMetaBaseline(null)
      return
    }

    setMetaBaseline(
      normalizeMetaForEntity(entityType, getMetaFromEntity(entityType, entity)),
    )
  }, [entityType, entity, isNew, shouldTrackMetaDirty])

  useEffect(() => {
    if (isNew || blocksLoading) return

    const blockHasChanges =
      Object.keys(draftStates).length > 0 ||
      localBlocks.length > 0 ||
      Object.entries(draftStates).some(([blockId, content]) => {
        const numericBlockId = Number(blockId)
        if (Number.isNaN(numericBlockId)) return false

        const originalBlock = blocks.find(
          (b) => Number(b.id) === numericBlockId,
        )
        if (!originalBlock) return false

        const originalContent =
          viewMode === 'draft'
            ? originalBlock.latest_revision?.content ||
              originalBlock.content ||
              {}
            : originalBlock.content || {}

        return JSON.stringify(content) !== JSON.stringify(originalContent)
      })

    const metaHasChanges =
      shouldTrackMetaDirty &&
      !!metaBaseline &&
      JSON.stringify(normalizeMetaForEntity(entityType, meta)) !==
        JSON.stringify(metaBaseline)

    setHasUnsavedChanges(blockHasChanges || metaHasChanges)
  }, [
    isNew,
    blocksLoading,
    blocks,
    draftStates,
    localBlocks,
    viewMode,
    shouldTrackMetaDirty,
    metaBaseline,
    entityType,
    meta,
    setHasUnsavedChanges,
  ])

  useEffect(() => {
    if (_hasHydrated && languages.length > 0 && !locale) {
      initializeLanguage(languages)
    }
  }, [languages, locale, initializeLanguage, _hasHydrated])

  useEffect(() => {
    return () => {
      if (isNew) {
        resetMeta()
        clearLocalBlocks()
      }
    }
  }, [isNew, resetMeta, clearLocalBlocks])

  const createMutation = useMutation({
    mutationFn: (data) => api.post(config.api.endpoints.create, data),
    onSuccess: async (response) => {
      const newEntityId = response.data.data.id

      if (localBlocks.length > 0) {
        const blockPromises = localBlocks.map((block) => {
          if (block.preset_id) {
            const presetBlockData = {
              [config.blockField]: newEntityId,
              preset_id: Number(block.preset_id),
              name: block.name,
              sort_order: block.sort_order,
              is_active: block.is_active ?? true,
              locale: locale,
            }
            return api.post('/blocks/create-from-preset', presetBlockData)
          }

          const localDraft = draftStates[block.id] || {}
          const mergedContent = { ...block.content, ...localDraft }
          const blockData = {
            [config.blockField]: newEntityId,
            block_type_id: block.block_type_id,
            name: block.name,
            content: mergedContent,
            settings: block.settings,
            sort_order: block.sort_order,
            locale: locale,
          }
          return api.post('/blocks', blockData)
        })

        try {
          await Promise.all(blockPromises)
        } catch (error) {
          alert(
            `${config.singularLabel} created but some blocks failed to save. Please try adding them again.`,
          )
        }
      }

      queryClient.invalidateQueries({
        queryKey: [config.api.queryKeys.list, locale],
      })
      clearLocalBlocks()
      navigate(`${config.routes.builder.replace(':id', newEntityId)}`, {
        replace: true,
      })
    },
    onError: (error) => {
      const message =
        error.response?.data?.message ||
        error.message ||
        `Failed to create ${config.singular}`
      alert(`Error: ${message}`)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data) =>
      api.put(config.api.endpoints.update.replace(':id', routeId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [config.api.queryKeys.single, routeId, locale],
      })
      queryClient.invalidateQueries({
        queryKey: [config.api.queryKeys.list, locale],
      })
    },
    onError: (error) => {
      const message =
        error.response?.data?.message ||
        error.message ||
        `Failed to update ${config.singular}`
      alert(`Error: ${message}`)
    },
  })

  const publishEntityMutation = useMutation({
    mutationFn: () => {
      if (!config.api.endpoints.publish) {
        return Promise.resolve()
      }
      return api.post(config.api.endpoints.publish.replace(':id', routeId))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [config.api.queryKeys.single, routeId, locale],
      })
      queryClient.invalidateQueries({
        queryKey: [config.api.queryKeys.list, locale],
      })
    },
    onError: (error) => {},
  })

  const handleSaveEntity = () => {
    const missingFields = config.fields.required.filter((field) => !meta[field])
    if (missingFields.length > 0) {
      const fieldLabels = missingFields.map((field) => {
        const fieldConfig = config.fields.sidebar.find((f) => f.name === field)
        return fieldConfig?.label || field
      })
      alert(`Please fill in required fields: ${fieldLabels.join(', ')}`)
      return
    }

    let payload = buildPayload()

    if (isNew) {
      if (
        entityType === 'product' &&
        meta.template_id &&
        localBlocks.length > 0
      ) {
        payload = { ...payload, skip_template_clone: true }
      }
      createMutation.mutate(payload)
    } else {
      updateMutation.mutate(payload)
    }
  }

  const handleSaveAll = useCallback(async () => {
    if (isNew) {
      await handleSaveEntity()
    } else {
      await updateMutation.mutateAsync(buildPayload())
      if (shouldTrackMetaDirty) {
        setMetaBaseline(normalizeMetaForEntity(entityType, meta))
      }

      handleSaveDrafts()
    }
  }, [
    isNew,
    handleSaveEntity,
    updateMutation,
    handleSaveDrafts,
    buildPayload,
    shouldTrackMetaDirty,
    entityType,
    meta,
  ])

  const handlePublishAll = useCallback(async () => {
    try {
      const payload = buildPayload()
      const publishPayload =
        entityType === 'template' ? { ...payload, is_active: true } : payload

      await updateMutation.mutateAsync(publishPayload)
      if (shouldTrackMetaDirty) {
        setMetaBaseline(normalizeMetaForEntity(entityType, meta))
      }

      await handlePublish()
      if (config.api.endpoints.publish) {
        await publishEntityMutation.mutateAsync()
      }
    } catch (error) {}
  }, [
    updateMutation,
    handlePublish,
    publishEntityMutation,
    buildPayload,
    config.api.endpoints.publish,
    entityType,
    shouldTrackMetaDirty,
    meta,
  ])

  const wrappedHandleDiscardDraft = useCallback(async () => {
    setIsDiscardingState(true)
    try {
      await handleDiscard()
      if (!isNew && entity && shouldTrackMetaDirty) {
        const resetMetaData = getMetaFromEntity(entityType, entity)
        setMeta(resetMetaData)
        setMetaBaseline(normalizeMetaForEntity(entityType, resetMetaData))
      }
    } finally {
      setIsDiscardingState(false)
    }
  }, [handleDiscard, isNew, entity, shouldTrackMetaDirty, entityType, setMeta])

  useEffect(() => {
    setSaveHandler(handleSaveEntity)
  }, [handleSaveEntity, setSaveHandler])

  useEffect(() => {
    setSaveDraftHandler(handleSaveAll)
  }, [handleSaveAll, setSaveDraftHandler])

  useEffect(() => {
    setPublishHandler(handlePublishAll)
  }, [handlePublishAll, setPublishHandler])

  useEffect(() => {
    setDiscardDraftHandler(wrappedHandleDiscardDraft)
  }, [wrappedHandleDiscardDraft, setDiscardDraftHandler])

  useEffect(() => {
    const savingState =
      createMutation.isPending || updateMutation.isPending || isSaving
    setIsSaving(savingState)
  }, [
    createMutation.isPending,
    updateMutation.isPending,
    isSaving,
    setIsSaving,
  ])

  useEffect(() => {
    const publishingState = isPublishing || publishEntityMutation.isPending
    setIsPublishing(publishingState)
  }, [isPublishing, publishEntityMutation.isPending, setIsPublishing])

  useEffect(() => {
    setIsDiscarding(isDiscardingState)
  }, [isDiscardingState, setIsDiscarding])

  const isValidId = routeId && (routeId === 'new' || !isNaN(Number(routeId)))

  if (!isValidId) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Invalid {config.singularLabel} ID
          </h2>
          <p className="text-gray-500 mb-4">
            The {config.singular} ID in the URL is invalid or missing.
          </p>
          <Button onClick={() => navigate(config.routes.list)}>
            Go to {config.pluralLabel}
          </Button>
        </div>
      </div>
    )
  }

  const updateBlockOrder = useCallback(
    (blockId, sortOrder) => {
      updateBlock.mutate({
        blockId,
        data: { sort_order: sortOrder },
      })
    },
    [updateBlock],
  )

  const handleDragEnd = (event) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const activeBlock = blocks.find((b) => b.id === active.id)
    const overBlock = blocks.find((b) => b.id === over.id)

    if (activeBlock?.is_fixed || overBlock?.is_fixed) return

    const regularBlocks = blocks.filter((b) => !b.is_fixed)
    const fixedBlocks = blocks.filter((b) => b.is_fixed)

    const oldIndex = regularBlocks.findIndex((block) => block.id === active.id)
    const newIndex = regularBlocks.findIndex((block) => block.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    const newRegularBlocks = arrayMove(regularBlocks, oldIndex, newIndex)

    newRegularBlocks.forEach((block, index) => {
      if (block.sort_order !== index) {
        updateBlockOrder(block.id, index)
      }
    })
  }

  useEffect(() => {
    setDraftStates({})
    setIsViewModeChanging(false)
  }, [routeId])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const blockId = urlParams.get('block')
    if (blockId) {
      setSelectedBlockId(Number(blockId))
    }
  }, [setSelectedBlockId])

  const handleBlockSelect = (block) => {
    setSelectedBlockId(block.id)
    const url = new URL(window.location)
    url.searchParams.set('block', block.id)
    window.history.replaceState({}, '', url)
  }

  useEffect(() => {
    setIsViewModeChanging(true)
    if (viewMode === 'live') {
      setDraftStates({})
    }

    const timer = setTimeout(() => {
      setIsViewModeChanging(false)
    }, 100)

    return () => clearTimeout(timer)
  }, [viewMode])

  useEffect(() => {
    setIsLocaleChanging(true)
    setDraftStates({})

    const timer = setTimeout(() => {
      setIsLocaleChanging(false)
    }, 200)

    return () => clearTimeout(timer)
  }, [locale])

  if (!_hasHydrated || !locale) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Initializing...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col">
          {(entityType === 'page' || entityType === 'post') &&
            !hasTranslation &&
            entity &&
            (entityType === 'post' ? (
              <PostTranslationBanner
                post={entity}
                locale={locale}
                languageName={languageName}
                onTranslationCreated={() => {
                  queryClient.invalidateQueries({
                    queryKey: [config.api.queryKeys.single, routeId, locale],
                  })
                }}
              />
            ) : (
              <TranslationBanner
                page={entity}
                locale={locale}
                languageName={languageName}
                onTranslationCreated={() => {
                  queryClient.invalidateQueries({
                    queryKey: [config.api.queryKeys.single, routeId, locale],
                  })
                }}
              />
            ))}

          {(isNew || viewMode === 'draft') && (
            <div className="flex-1 overflow-auto p-6">
              {(entityType === 'product' || entityType === 'template') && (
                <div className="max-w-6xl mx-auto mb-6">
                  <ProductMetaTabs
                    entity={entity}
                    entityType={entityType}
                    isNew={isNew}
                  />
                </div>
              )}
              <div className="max-w-4xl mx-auto">
                {!isNew && blocksLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading blocks...</p>
                  </div>
                ) : blocks.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Plus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No blocks yet
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Add your first block using the sidebar
                    </p>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={blocks
                        .filter((b) => !b.is_fixed)
                        .map((block) => block.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-4">
                        {blocks.map((block, index) => (
                          <SortableBlock
                            key={block.id}
                            block={block}
                            index={index}
                            isSelected={selectedBlockId === block.id}
                            onSelect={handleBlockSelect}
                            viewMode={viewMode}
                            onFieldChange={handleFieldChange}
                            draftStates={draftStates}
                            locale={locale}
                            readOnly={
                              (isNew ? false : !hasTranslation) ||
                              !!block.is_preset_synced
                            }
                            onDetachPreset={handleDetachPreset}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>
          )}
          {!isNew && viewMode === 'live' && (
            <div className="flex-1 overflow-auto p-6">
              <div className="max-w-4xl mx-auto">
                {blocksLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading blocks...</p>
                  </div>
                ) : blocks.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No published content
                    </h3>
                    <p className="text-gray-500 mb-4">
                      This {config.singular} has no published blocks yet
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {blocks.map((block, index) => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        index={index}
                        isSelected={selectedBlockId === block.id}
                        onSelect={handleBlockSelect}
                        viewMode={viewMode}
                        onFieldChange={handleFieldChange}
                        draftStates={draftStates}
                        locale={locale}
                        readOnly={!hasTranslation || !!block.is_preset_synced}
                        onDetachPreset={handleDetachPreset}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {(entityType === 'page' || entityType === 'post') && (
          <ContentMetaSidebar
            entity={entity}
            entityType={entityType}
            isNew={isNew}
            locale={locale}
          />
        )}
      </div>
    </div>
  )
}
